import { PrintStatus } from '../components/Print.functions';
import { ClimateLocationsDto } from '../components/InputFieldsContainer';
import { getConfig } from '../config/envconfig';
import FileSaver from 'file-saver';
import { TranslationsDto } from '../shared/contexts/TranslationContext';
import { ProductVariantDataDto } from '../types/ProductVariant.types';

export interface SelectedValuesDto {
    market: string;
    productVariant: string;
    sizeId: string;
    sizeName: string;
    sizeNamePadded: string;
    airflowUnit: string;
    supply: {
        airflow: number;
        pressureDrop: number;
    };
    extract: {
        airflow: number;
        pressureDrop: number;
    };
    outdoorPressureDrop: number;
    exhaustPressureDrop: number;
    climateLocation: {
        countryKey: number;
        cityKey: number;
    };
    coil: string;
    dampers: string[];
}

class backendApi {
    private readonly url: string;
    private readonly authToken: string;
    private readonly useCors = getConfig()?.useCors === true;

    constructor(baseUrl: string, authToken: string) {
        this.url = baseUrl;
        this.authToken = authToken;
    }

    fetchFromBff = async <T>(url: URL): Promise<T> => {
        return new Promise<T>(async (resolve, reject) => {
            await fetch(url, {
                headers: {
                    Authorization: `Bearer ${this.authToken}`,
                    'Accept-Language': navigator.language
                },
                mode: this.useCors ? 'cors' : 'no-cors'
            })
                .then((response) => {
                    if (response.status === 200) {
                        resolve(response.json());
                    } else {
                        const match = response.url.match('.+liteapp/(.+)');
                        const endpoint = match ? match[1] : response.url;
                        throw new Error(
                            `Server responded with status code ${response.status} for call to "${endpoint}"`
                        );
                    }
                })
                .catch((error) => {
                    reject(error.statusText || error.message);
                });
        });
    };

    postToBff = async <T>(url: URL, body: string): Promise<T | void> => {
        return new Promise<T | void>(async (resolve, reject) => {
            await fetch(url, {
                headers: {
                    Authorization: `Bearer ${this.authToken}`,
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Accept-Language': navigator.language
                },
                mode: this.useCors ? 'cors' : 'no-cors',
                method: 'POST',
                body: body
            })
                .then(async (response) => {
                    if (response.status === 200) {
                        const contentType =
                            response.headers.get('content-type');
                        if (
                            contentType &&
                            contentType.includes('application/json')
                        ) {
                            resolve(response.json());
                        } else {
                            resolve();
                        }
                    } else {
                        throw new Error(
                            `Server responded with status code ${response.status}`
                        );
                    }
                })
                .catch((error) => {
                    reject(error.statusText || error.message);
                });
        });
    };

    getTranslations = async (market: string) => {
        const url = new URL(
            `${this.url}Application/Translations?market=${market}`
        );
        return this.fetchFromBff<TranslationsDto>(url);
    };

    getProductVariant = async (searchParams: URLSearchParams) => {
        const url = new URL(
            `${this.url}ProductVariant?${searchParams.toString()}`
        );
        return this.fetchFromBff<ProductVariantDataDto>(url);
    };

    startCalculation = async (requestBody: SelectedValuesDto) => {
        const url = new URL(`${this.url}Calculation/Start`);
        return this.postToBff<string>(
            url,
            JSON.stringify(requestBody)
        ) as Promise<string>;
    };

    createLiteAhuJob = async (requestBody: SelectedValuesDto) => {
        const url = new URL(`${this.url}CreateAhu/Create`);
        return this.postToBff<string>(
            url,
            JSON.stringify(requestBody)
        ) as Promise<string>;
    };

    sendEmail = async (
        market: string,
        name: string,
        email: string,
        phone: string,
        message: string,
        shareLink: string,
        country: string,
        city: string,
        companyName: string,
        typeOfBusiness: string,
        productInfoDto: { productVariant: string; sizeName: string }
    ) => {
        const requestBody = JSON.stringify({
            market: market,
            name: name,
            email: email !== '' ? email : undefined,
            phone: phone !== '' ? phone : undefined,
            message: message,
            shareLink: shareLink,
            country: country,
            city: city,
            companyName: companyName,
            typeOfBusiness: typeOfBusiness,
            productInfoDto
        });
        const url = new URL(`${this.url}Share/ContactSales`);
        return this.postToBff<void>(url, requestBody) as Promise<void>;
    };

    sendPrintRequest = async (
        market: string,
        airflowUnit: string,
        documentType: string,
        jobId: string,
        productVariant: string,
        printRequestTimeout: number = 40000
    ) => {
        const requestBody = JSON.stringify({
            market: market,
            airflowUnit: airflowUnit,
            documentType: documentType,
            jobId: jobId,
            productVariant: productVariant
        });

        return new Promise<string>(async (resolve, reject) => {
            const timeout = new Promise((_, rejectTimeout) => {
                setTimeout(() => {
                    rejectTimeout('Request timed out');
                }, printRequestTimeout);
            });

            const fetchPromise = fetch(`${this.url}Print`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${this.authToken}`,
                    'Content-Type': 'application/json;charset=UTF-8'
                },
                mode: this.useCors ? 'cors' : 'no-cors',
                body: requestBody
            })
                .then(async (response) => {
                    if (response.status === 200) {
                        resolve(await response.json());
                    } else {
                        reject('Failed to start print');
                    }
                })
                .catch((error) => {
                    reject(error.statusText || error.message);
                });

            Promise.race([fetchPromise, timeout]).catch((error) => {
                reject(error);
            });
        });
    };

    getPrintDocument = async (
        jobId: string,
        documentType: string,
        productVariant: string
    ) => {
        // This functions converts the base64 representation of the document file bytes back to
        // an array of bytes that the Blob object expects and then returns a Blob object
        const createBlob = (fileBytes: string, mimeType: string): Blob => {
            const byteCharacters = atob(fileBytes);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            return new Blob([byteArray], { type: mimeType });
        };

        return new Promise<void>(async (resolve, reject) => {
            await fetch(
                `${this.url}Print?jobId=${jobId}&documentType=${documentType}&productVariant=${productVariant}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authToken}`
                    },
                    mode: this.useCors ? 'cors' : 'no-cors'
                }
            )
                .then(async (response) => {
                    if (response.status === 200) {
                        const data = await response.json();
                        const blob = createBlob(data.fileBytes, data.mimeType);
                        FileSaver.saveAs(blob, data.fileName);
                        resolve();
                    } else {
                        reject('Failed to fetch document');
                    }
                })
                .catch((error) => {
                    reject(error.statusText || error.message);
                });
        });
    };

    getPrintStatus = async (jobId: string) => {
        const url = new URL(`${this.url}Print/Status/?jobId=${jobId}`);
        return this.fetchFromBff<PrintStatus>(url);
    };

    getClimateLocations = async (market: string) => {
        const url = new URL(`${this.url}Climate/Locations?market=${market}`);
        return this.fetchFromBff<ClimateLocationsDto>(url);
    };
}

export default backendApi;
